import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import type { UrdfBindableMessagePath } from "@/shared/domain/urdf";

/**
 * The message paths of a single topic a joint binding may name, each with the
 * type and unit the topic's schema versions agree on. Disabled until both an org
 * and a non-empty `topicName` are available, so callers can pass `null` while
 * the user hasn't settled on a topic yet. Cached per `(orgId, topicName)`.
 *
 * See `UrdfBindableMessagePath`: every attribute but the path is optional, and
 * an environment predating the endpoint resolves to no paths at all.
 */
export function useUrdfBindableMessagePaths(
  topicName: string | null,
  orgId?: string,
) {
  const { currentOrganization } = useAuth();
  const canonicalOrgId = orgId || currentOrganization?.org_id;
  const { urdf: urdfService } = useDomainServices();

  return useQuery<UrdfBindableMessagePath[], Error>({
    queryKey: ["urdfBindableMessagePaths", canonicalOrgId, topicName],
    queryFn: ({ signal }) => {
      if (canonicalOrgId === undefined || !topicName) {
        return Promise.reject(new Error("Invalid orgId or topicName"));
      }

      return urdfService.getBindableMessagePaths(topicName, {
        abortSignal: signal,
        resourceOwnerId: canonicalOrgId,
      });
    },
    enabled: canonicalOrgId !== undefined && !!topicName,
    staleTime: 30000,
  });
}
